import { TAG_COLORS, type TagColor, TagFormSchema } from '@fundline/shared/schemas/fields';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { TagChip } from '../../components/ui/TagChip.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { SettingsTabs } from './SettingsTabs.tsx';
import { type Tag, useTagMutations, useTagsQuery } from './use-tags.ts';

const COLOR_OPTIONS = TAG_COLORS.map((c) => ({
  value: c,
  label: c.charAt(0).toUpperCase() + c.slice(1),
}));

function asColor(value: string): TagColor {
  return (TAG_COLORS as readonly string[]).includes(value) ? (value as TagColor) : 'gray';
}

export function TagsSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const tags = useTagsQuery(workspaceId);
  const { create, update, remove } = useTagMutations(workspaceId);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, success: string): Promise<boolean> {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(success);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      return false;
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Tags label merchants. Reps add and remove them; only admins create, rename or delete them."
      />
      <SettingsTabs />

      <Card title="Add a tag">
        <TagForm
          submitLabel="Add tag"
          busy={create.isPending}
          onSubmit={(input) => run(() => create.mutateAsync(input), `Added "${input.name}".`)}
        />
      </Card>

      <section className="flex flex-col gap-3" aria-label="Tags">
        <h2 className="body font-semibold text-ink">Tags</h2>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}
        {tags.isPending ? <Spinner label="Loading tags" /> : null}
        {tags.isError ? <Alert tone="error">{tags.error.message}</Alert> : null}
        {tags.data?.length === 0 ? <EmptyState title="No tags yet" /> : null}
        {tags.data && tags.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {tags.data.map((tag: Tag) => (
              <li key={tag.id} className="p-3" data-testid="tag-row">
                {editing === tag.id ? (
                  <TagForm
                    initial={{ name: tag.name, color: asColor(tag.color) }}
                    nameLocked={tag.isSystem}
                    submitLabel="Save"
                    busy={update.isPending}
                    onCancel={() => setEditing(null)}
                    onSubmit={async (input) => {
                      const ok = await run(
                        () =>
                          update.mutateAsync({
                            id: tag.id,
                            patch: tag.isSystem ? { color: input.color } : input,
                          }),
                        'Tag saved.',
                      );
                      if (ok) setEditing(null);
                    }}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <TagChip name={tag.name} color={tag.color} />
                      {tag.isSystem ? (
                        <span className="footnote text-ink-muted">
                          Built-in, set automatically later by the AI
                        </span>
                      ) : null}
                    </span>
                    <span className="flex gap-1">
                      <Button
                        variant="secondary"
                        aria-label={`Edit tag ${tag.name}`}
                        onClick={() => setEditing(tag.id)}
                      >
                        Edit
                      </Button>
                      {tag.isSystem ? null : (
                        <Button
                          variant="danger"
                          size="sm"
                          aria-label={`Delete tag ${tag.name}`}
                          disabled={remove.isPending}
                          onClick={() => {
                            if (
                              !window.confirm(
                                `Delete "${tag.name}"? It is removed from every merchant that has it.`,
                              )
                            )
                              return;
                            void run(() => remove.mutateAsync(tag.id), `Deleted "${tag.name}".`);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

function TagForm({
  initial = { name: '', color: 'gray' },
  nameLocked = false,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: { name: string; color: TagColor };
  nameLocked?: boolean;
  submitLabel: string;
  busy: boolean;
  onSubmit: (input: { name: string; color: TagColor }) => Promise<unknown>;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [color, setColor] = useState<TagColor>(initial.color);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = TagFormSchema.safeParse({ name, color });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    const ok = await onSubmit(parsed.data);
    if (!onCancel && ok !== false) {
      setName('');
      setColor('gray');
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => void submit(e)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel?.();
      }}
      noValidate
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          label="Tag name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          disabled={nameLocked}
          hint={nameLocked ? 'Built-in tags keep their name.' : undefined}
        />
        <SelectField
          label="Color"
          value={color}
          onChange={(e) => setColor(asColor(e.target.value))}
          options={COLOR_OPTIONS}
          error={errors.color}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" busy={busy}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <span className="ml-1">
          <TagChip name={name.trim() || 'Preview'} color={color} />
        </span>
      </div>
    </form>
  );
}
